// Živé Overview a Dashboard (jako Terminal, bez překreslování grafů).
// Stránka pro aktuální období má <div data-live-root data-live-url="/app/live/…" data-live-ws="sp">.
// Po nové zprávě nebo změně sentimentu (událost mx:live ze socket.js; navíc každou minutu)
// se stáhnou HTML „živé oblasti“ (data-live="…", moodix/web/live.py) a vymění se na místě. Změněná čísla
// (data-num) se plynule dopočítají a bliknou podle směru, nové řádky zpráv (data-id) sjedou shora.
import { localizeTimes } from './localtime.js';

const POLL_MS = 60e3;
const DEBOUNCE_MS = 1500;
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ---------------------------------------------------------------- formát čísel (jako filtry v moodix/templatetags/ui.py)
const MINUS = '−';
const fixed = (v, d) => Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const signed = (v, d) => `${v > 0 ? '+' : v < 0 ? MINUS : ''}${fixed(v, d)}`;
function decSplit(v) {
  const [whole, dec] = fixed(v, 2).split('.');
  return `${v < 0 ? MINUS : ''}${whole}<span class="dec">.${dec}</span>`;
}
const FORMATS = {
  dec: (v) => decSplit(v),
  sdec: (v) => `${v >= 0 ? '+' : ''}${decSplit(v)}`,
  signed: (v, d) => signed(v, d),
  pct: (v, d) => `${signed(v, d)}%`,
  int: (v) => String(Math.round(v)),
};

function tween(el, from, to) {
  const fmt = FORMATS[el.dataset.fmt];
  if (!fmt) return;
  const d = Number(el.dataset.dec ?? 2);
  if (reduced()) { el.innerHTML = fmt(to, d); return; }
  const start = performance.now();
  const step = (t) => {
    const k = Math.min(1, (t - start) / 700);
    const e = 1 - (1 - k) ** 3;
    el.innerHTML = fmt(from + (to - from) * e, d);
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function flash(el, cls) {
  el.classList.remove('num-up', 'num-down', 'word-flip');
  void el.offsetWidth; // restart animace
  el.classList.add(cls);
}

/** Vymění obsah oblasti a animuje změny proti předchozímu stavu. */
function swap(el, html) {
  const oldNums = [...el.querySelectorAll('[data-num]')].map((n) => Number(n.dataset.num));
  const oldWords = Object.fromEntries([...el.querySelectorAll('[data-word]')].map((w) => [w.dataset.word, w.textContent.trim()]));
  const oldIds = new Set([...el.querySelectorAll('[data-id]')].map((r) => r.dataset.id));
  el.innerHTML = html;
  window.htmx?.process(el);
  localizeTimes(el);
  el.querySelectorAll('[data-num]').forEach((n, i) => {
    const to = Number(n.dataset.num);
    const from = oldNums[i];
    if (from === undefined || Number.isNaN(from) || Number.isNaN(to) || from === to) return;
    tween(n, from, to);
    flash(n, to > from ? 'num-up' : 'num-down');
  });
  el.querySelectorAll('[data-word]').forEach((w) => {
    if (oldWords[w.dataset.word] !== undefined && oldWords[w.dataset.word] !== w.textContent.trim()) flash(w, 'word-flip');
  });
  if (oldIds.size) markNew(el, oldIds);
}

function markNew(root, oldIds) {
  root.querySelectorAll('[data-id]').forEach((r) => { if (!oldIds.has(r.dataset.id)) r.classList.add('row-new'); });
}

// ---------------------------------------------------------------- obnovení
async function refreshRegions(url) {
  const res = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } });
  if (!res.ok) return;
  const { regions, charts } = await res.json();
  if (charts && Object.keys(charts).length) {
    try { (await import('../charts/index.js')).updateCharts(charts); } catch (err) { console.warn('Chart refresh failed', err); }
  }
  for (const [key, html] of Object.entries(regions || {})) {
    const el = document.querySelector(`[data-live="${key}"]`);
    if (el && !el.closest('.is-editing')) swap(el, html);
  }
}

/** Výpis zpráv Dashboardu: jen první stránka bez hledání (jinak by výpis uživateli poskakoval). */
async function refreshFeed() {
  const feed = document.getElementById('feed');
  if (!feed?.dataset.liveFeed || feed.dataset.page !== '1' || feed.dataset.query) return;
  if (feed.contains(document.activeElement) && document.activeElement.matches('input, select')) return;
  const res = await fetch(feed.dataset.liveFeed, { credentials: 'same-origin', headers: { 'HX-Request': 'true' } });
  if (!res.ok) return;
  const tpl = document.createElement('template');
  tpl.innerHTML = (await res.text()).trim();
  const next = tpl.content.getElementById('feed');
  if (!next) return;
  const oldIds = new Set([...feed.querySelectorAll('[data-id]')].map((r) => r.dataset.id));
  feed.replaceWith(next);
  window.htmx?.process(next);
  localizeTimes(next);
  markNew(next, oldIds);
}

export function initLive() {
  const root = document.querySelector('[data-live-root]');
  if (!root?.dataset.liveUrl) return;
  let timer = null;
  let busy = false;
  const refresh = async () => {
    if (busy || document.visibilityState !== 'visible') return;
    busy = true;
    try { await Promise.all([refreshRegions(root.dataset.liveUrl), refreshFeed()]); } catch (err) { console.warn('Live refresh failed', err); } finally { busy = false; }
  };
  const schedule = () => { clearTimeout(timer); timer = setTimeout(refresh, DEBOUNCE_MS); };

  setInterval(refresh, POLL_MS);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') schedule(); });

  // Události ze společného spojení (socket.js) pro trhy, které stránka ukazuje (data-live-ws="sp", "oil", "sp,oil")
  const markets = (root.dataset.liveWs || '').split(',').filter(Boolean);
  window.addEventListener('mx:live', (e) => { if (markets.includes(e.detail.market)) schedule(); });
}
